import { useMemo } from 'react';

import type { OrgSubUnit, SubUnitType } from '@appTypes/esb';
import { buildDenLevelImages } from '@modules/roster-v2/models/rosterMemberView';
import { useGetRanksQuery, useGetSubUnitsQuery } from '@modules/rtk/esb-api';

interface UseSubUnitGroupsSourceInput {
  orgGuid?: string;
  subUnitType?: SubUnitType;
  /** Grouping is offered: nothing is fetched otherwise */
  enabled: boolean;
  grouped: boolean;
}

export interface SubUnitGroupsSource {
  /** `undefined` until `getSubUnits` answers, or when it fails */
  orgSubUnits?: OrgSubUnit[];
  denLevelImages: Partial<Record<string, string>>;
  /** `getSubUnits` answered (data or error) or was skipped */
  settled: boolean;
}

/**
 * `getSubUnits`, so empty dens/patrols get a group, and the den rank images.
 * If it fails, the groups come from the members' own sub-units.
 */
export function useSubUnitGroupsSource(
  input: UseSubUnitGroupsSourceInput,
): SubUnitGroupsSource {
  const { orgGuid, subUnitType, enabled, grouped } = input;
  const skipSubUnits = !enabled || !orgGuid;

  // `currentData`, so an org switch doesn't show the old unit's sub-units
  const { currentData: orgSubUnits, isError } = useGetSubUnitsQuery(
    { orgGuid: orgGuid ?? '' },
    { skip: skipSubUnits },
  );
  const { data: ranks } = useGetRanksQuery(undefined, {
    skip: !enabled || !grouped || subUnitType !== 'den',
  });
  const denLevelImages = useMemo(() => buildDenLevelImages(ranks), [ranks]);

  return {
    orgSubUnits,
    denLevelImages,
    settled: skipSubUnits || orgSubUnits !== undefined || isError,
  };
}
